import React, { useState, useEffect, useContext } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import Heading from '@atlaskit/heading';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProformaProjectFormsOnMount } from '@atlassian/jira-proforma-request-type-config/src/services/proforma-project-forms/index.tsx';
import { AsyncFormEmptyState } from '@atlassian/jira-proforma-request-type-config/src/ui/form-empty-state/async.tsx';
import { FormsNotLoadedError } from '@atlassian/jira-proforma-request-type-config/src/ui/form-empty-state/index.tsx';
import type { ProformaRequestTypeFormCardConfig } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types.tsx';
import type { FieldCategoryWithFields } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/layout-items.tsx';
import type { Form } from '@atlassian/proforma-form-system/add-form';
import { fg } from '@atlassian/jira-feature-gating';
import { useTicketTypeStore } from '@atlassian/jira-servicedesk-ticket-types-store/src/controllers/index.tsx';
import { FormRestrictionStatus } from '@atlassian/proforma-form-security';
import { ProformaStoreContext } from '../../common/utils/proforma/context/index.tsx';
import { setSelectedForm } from '../../common/utils/proforma/ops/set-selected-form/index.tsx';
import { markProformaReqTypeFormForRemoval } from '../../common/utils/proforma/state/actions/index.tsx';
import {
	getProformaRequestTypeFormCardConfig,
	isProformaReqTypeConfigLoading,
} from '../../common/utils/proforma/state/selectors.tsx';
import type { State } from '../../common/utils/proforma/state/types.tsx';
import { FormCard } from './form-card/index.tsx';
import { messages } from './messages.tsx';

export interface FormPanelProps {
	autoExpandFormCard?: boolean;
	category: FieldCategoryWithFields;
	projectKey: string;
	onSetHasSeenEmptyStatePulse: () => void;
}

export interface ProformaFormHeaderProps {
	requestTypeId: number;
	formRestrictionStatus?: FormRestrictionStatus;
}

// FormSecurityWarningIcon can be replaced with a platform component for consistency with warning
// icons used in the proforma-form-builder.
const FormSecurityWarningIcon = () => {
	const { formatMessage } = useIntl();
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const oldIconLabel = fg('itsm-issue-renaming')
		? formatMessage(messages.formSecurityWithIssueSecurityWarningLabelIssueTermRefresh)
		: formatMessage(messages.formSecurityWithIssueSecurityWarningLabel);
	return (
		<Popup
			isOpen={isPopupOpen}
			onClose={() => setIsPopupOpen(false)}
			placement="bottom-end"
			messageId="project-settings-product-features-jsm-common.ui.form-panel.popup"
			messageType="transactional"
			content={() => (
				<Box padding="space.200" xcss={warningPopupContentStyles}>
					<FormattedMessage
						{...(fg('itsm-issue-renaming')
							? messages.formSecurityWithIssueSecurityWarningDescriptionIssueTermRefresh
							: messages.formSecurityWithIssueSecurityWarningDescription)}
					/>
				</Box>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					icon={(iconProps) => (
						<WarningIcon
							{...iconProps}
							color={isPopupOpen ? token('color.icon.selected') : token('color.icon.warning')}
						/>
					)}
					isSelected={isPopupOpen}
					onClick={() => setIsPopupOpen(!isPopupOpen)}
					label={
						fg('granular_permissions_on_forms_ga')
							? formatMessage(messages.formSecurityAndIssueSecurityWarningLabel)
							: oldIconLabel
					}
					appearance="subtle"
					spacing="compact"
				/>
			)}
		/>
	);
};

const ProformaFormHeaderOld = () => {
	const { formatMessage } = useIntl();
	return (
		<>
			<Line />
			<Box xcss={sectionSpacingStyles}>
				<Heading size="xxsmall" as="div">
					{formatMessage(messages.attachedFormLabel)}
				</Heading>
			</Box>
		</>
	);
};

const ProformaFormHeader = ({ requestTypeId, formRestrictionStatus }: ProformaFormHeaderProps) => {
	const { formatMessage } = useIntl();
	const [requestType] = useTicketTypeStore(requestTypeId);

	return (
		<>
			<Line />
			<Box xcss={sectionSpacingStyles}>
				<Inline space="space.025" alignBlock="center">
					<Heading size="xxsmall" as="div">
						{formatMessage(messages.attachedFormLabel)}
					</Heading>
					{requestType?.issueSecurityStatus === 'ACTIVE' &&
						formRestrictionStatus === FormRestrictionStatus.RESTRICTED && (
							<FormSecurityWarningIcon />
						)}
				</Inline>
			</Box>
		</>
	);
};

export const FormPanel = (props: FormPanelProps) => {
	const { category, projectKey, onSetHasSeenEmptyStatePulse, autoExpandFormCard = false } = props;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { forms, isLoading, error } = useProformaProjectFormsOnMount();

	const shouldShowFormEmptyState = expValEquals(
		'rtc_form_empty_state_to_all_user',
		'cohort',
		'experiment',
	)
		? !isLoading && !error
		: forms.length > 0;

	useEffect(() => {
		if (expVal('rtc_form_empty_state_to_all_user', 'cohort', 'not-enrolled') !== 'not-enrolled') {
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'statsigExperiment exposed',
				'rtc_form_empty_state_to_all_user',
			);
		}
	}, [createAnalyticsEvent]);

	const [isNewTemplateFormCreated, setIsNewTemplateFormCreated] = useState(false);

	const proformaStore = useContext(ProformaStoreContext);
	const [proformaState, setProformaState] = useState<State>();
	const [proformaConfig, setProformaConfig] = useState<ProformaRequestTypeFormCardConfig>();

	useEffect(() => {
		const unsubscribe = proformaStore
			? proformaStore.subscribe(() => {
					const state = proformaStore.getState();
					setProformaState(state);
					const config = getProformaRequestTypeFormCardConfig(state);
					setProformaConfig(config);
				})
			: noop;

		// for initial fetch - if the store's state is loaded after component mount, this will ensure it receives the current state on mount.
		if (proformaStore && !proformaState && !proformaConfig) {
			const state = proformaStore.getState();
			setProformaState(state);
			const config = getProformaRequestTypeFormCardConfig(state);
			setProformaConfig(config);
		}

		// Clean up the subscription
		return () => {
			unsubscribe();
		};
	}, [proformaConfig, proformaState, proformaStore]);

	const onRemoveFormExample = (requestTypeId: number) => {
		proformaStore?.dispatch(markProformaReqTypeFormForRemoval(requestTypeId));
	};

	const onAddFormExample = async (optimisticForm: Form) => {
		if (proformaStore) {
			setSelectedForm(proformaStore, optimisticForm.value);
		}
	};

	if (proformaState && isProformaReqTypeConfigLoading(proformaState)) {
		return <Spinner />;
	}

	return (
		<>
			{proformaConfig && (
				<>
					{fg('granular_permissions_on_forms_m1') ? (
						<ProformaFormHeader
							requestTypeId={proformaConfig.requestTypeId}
							formRestrictionStatus={proformaConfig.restrictionStatus}
						/>
					) : (
						<ProformaFormHeaderOld />
					)}
					<FormCard
						config={proformaConfig}
						onRemoveFormFromReqType={onRemoveFormExample}
						category={category}
						projectKey={projectKey}
						autoExpand={autoExpandFormCard}
					/>
				</>
			)}
			{!proformaConfig && (
				<>
					{shouldShowFormEmptyState && (
						<>
							<Line />
							<AsyncFormEmptyState
								onTemplateFormCreated={() => {
									setIsNewTemplateFormCreated(true);
								}}
								onAdd={onAddFormExample}
								onPulseShown={onSetHasSeenEmptyStatePulse}
								hasTemplateForms={isNewTemplateFormCreated || forms.length > 0}
							/>
						</>
					)}
					{isLoading && <div />}
					{!!error && (
						<>
							<Line />
							<FormsNotLoadedError />
						</>
					)}
				</>
			)}
		</>
	);
};

export const FormPanelWithErrorBoundary = (props: FormPanelProps) => (
	<JSErrorBoundary
		id="jsm-proforma-form-panel"
		packageName="jiraProjectSettingsProductFeaturesJsmCommon"
		teamName="jsm-request-intake"
		fallback="flag"
	>
		<FormPanel {...props} />
	</JSErrorBoundary>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Line = styled.div({
	borderBottom: `1px solid ${token('color.border')}`,
});

const sectionSpacingStyles = xcss({
	marginTop: 'space.100',
});

const warningPopupContentStyles = xcss({
	maxWidth: '200px',
});
